// Mounts the assistant panel, once, for the whole app.
//
// Lives in the layout rather than in the header so it outlives route changes:
// the conversation is in the store, but the panel that shows it has to survive
// navigation too, or "Take me there" would close the assistant the moment it
// was useful.
//
// Also the single owner of the availability check, so the desktop and mobile
// launchers read one answer instead of asking twice per page.

import React, { useEffect } from "react";
import Drawer from "components/ui-component/SideDrawer";
import { useMediaQuery } from "react-responsive";
import { useAppDispatch } from "store/hooks";
import { setAiAvailable, setAiOpen, useAiState } from "store/slices/aiSlice";
import { useUserData } from "store/slices/authSlice";
import AiPanel from "./AiPanel";
import { fetchAiSession } from "./aiApi";

const AiPanelHost = () => {
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const { available, open } = useAiState();
  const isNarrow = useMediaQuery({ query: "(max-width:590px)" });

  const token = userData?.token;
  const firstName = userData?.data?.accountData?.first_name;

  useEffect(() => {
    if (!token) {
      dispatch(setAiAvailable(false));
      return undefined;
    }

    const controller = new AbortController();
    fetchAiSession(controller.signal).then((ok) => {
      if (!controller.signal.aborted) dispatch(setAiAvailable(ok));
    });

    return () => controller.abort();
  }, [token, dispatch]);

  // Escape closes it, as with any other overlay on the page.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dispatch(setAiOpen(false));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, dispatch]);

  if (!token || !available) return null;

  return (
    <Drawer
      enableOverlay={true}
      lockBackgroundScroll={true}
      direction="right"
      open={open}
      size={isNarrow ? "100vw" : "480px"}
      onClose={() => dispatch(setAiOpen(false))}
      overlayColor="#000"
    >
      <AiPanel
        open={open}
        onClose={() => dispatch(setAiOpen(false))}
        firstName={firstName}
      />
    </Drawer>
  );
};

export default AiPanelHost;
